import { Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CommonModule } from '@angular/common';
import { 
  Component, 
  ChangeDetectionStrategy, 
  OnDestroy, 
  Inject, 
  ChangeDetectorRef 
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';

import { ISnListResponse, ISnListQueryParameters } from '@lib/api';
import { SnIncident } from '@lib/api/entities';
import { SnIncidentsAPI } from '@lib/api/services';
import { SnFormModule } from '@lib/form';
import { AddNewIncidentComponent } from '@lib/incidents';
import { t, TranslationModule, TranslationService } from '@lib/translation';
import { SnIconModule } from '@lib/ui/icon';

@Component({
  selector: 'fs-media-message-vaults',
  templateUrl: './send-media-to-vault.dialog.html',
  styleUrls: ['./send-media-to-vault.dialog.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatTooltipModule,
    TranslationModule,
    SnFormModule,
    SnIconModule,
  ],
})
export class MediaMessageVaultsDialogComponent implements OnDestroy {
  public form: UntypedFormGroup = new UntypedFormGroup({});
  public formModel: any = {};
  public formFields: any[] = [];

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _dialogRef: MatDialogRef<MediaMessageVaultsDialogComponent>,
    private readonly _matDialog: MatDialog,
    private readonly _incidentsAPI: SnIncidentsAPI,
    private readonly _cd: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: {
      file: any;
      needsUpload?: boolean;
    },
  ) {
    this.formFields = [
      {
        key: 'id',
        type: 'http-select',
        templateOptions: {
          label: t('common.caseNumber'),
          valueProp: 'id',
          labelProp: 'caseNumber',
          emptyOption: false,
          fetchDataFn: this._fetchData.bind(this),
        },
      },
    ];
  }

  public submit(closeTelestration?: boolean): void {
    if (!this.data.needsUpload) {
      const formData = this.form.value;
      const payload = { file_url: this.data };
      this._incidentsAPI
        .createVaultIncidentMedia(formData.id, payload)
        .pipe(takeUntil(this._destroy$))
        .subscribe(() => {
          this._dialogRef.close();
        });
    }
    const data = {
      id: this.form.value,
      closeTelestration,
    };
    this._dialogRef.close(data);
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public cancel(): void {
    this._dialogRef.close();
  }

  public addNewIncident(): void {
    const dialog = this._matDialog.open(AddNewIncidentComponent);

    dialog
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((res) => {
        if (res) {
          this.formModel = res;
          this._cd.detectChanges();
        }
      });
  }

  private _fetchData(params: ISnListQueryParameters): Observable<ISnListResponse<SnIncident>> {
    return this._incidentsAPI.getVaultIncidentList(params);
  }
}
